import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { apiClient } from "../api/client.js";

// Vaihe UM.6.B.1 — view-scope context.
//
// Admin valitsee nakymalleen:
//   "self"          (default kaikille mukaan lukien admin)
//   "all"           (admin: kaikki projektit, bypass)
//   "user:<id>"     (admin: simuloi X:n nakymaa)
//
// Tallennetaan localStorage:ssa jotta pysyy reload:n yli.
// apiClient injektoi headerin "X-Kratti-View-Scope" jokaiseen pyyntöön.

const STORAGE_KEY = "kratti.viewScope.v1";

export type ViewScope =
  | { kind: "self" }
  | { kind: "all" }
  | { kind: "user"; userId: string; label: string };

type ViewScopeContextValue = {
  scope: ViewScope;
  setScope: (scope: ViewScope) => void;
  /** Header-arvo joka apiClient lisää pyyntöön. */
  headerValue: string;
  /** True kun ei oletustila (self). */
  isOverride: boolean;
};

const Context = createContext<ViewScopeContextValue | null>(null);

function loadFromStorage(): ViewScope {
  if (typeof window === "undefined") return { kind: "self" };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { kind: "self" };
    const parsed = JSON.parse(raw) as ViewScope;
    if (parsed.kind === "self" || parsed.kind === "all") return parsed;
    if (parsed.kind === "user" && typeof parsed.userId === "string") return parsed;
  } catch {
    // ignore
  }
  return { kind: "self" };
}

function serializeForHeader(scope: ViewScope): string {
  if (scope.kind === "self") return "self";
  if (scope.kind === "all") return "all";
  return `user:${scope.userId}`;
}

export function ViewScopeProvider({ children }: { children: ReactNode }) {
  const [scope, setScopeState] = useState<ViewScope>(loadFromStorage);

  const setScope = useCallback((next: ViewScope) => {
    setScopeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // localStorage saattaa olla disabled (private mode)
    }
  }, []);

  // KRIITTINEN: aseta apiClient:n scope-getter SYNKRONISESTI render-body:ssa
  // (ei useEffect:ssa). Lasten useEffect-pohjaiset fetch:t ajetaan ENNEN
  // taeltaen useEffect:ia, joten useEffect-pohjainen asetus aiheuttaisi
  // ekan fetch:n menemisen ilman scope-headeria.
  apiClient.setViewScopeProvider(() => serializeForHeader(scope));

  const value = useMemo<ViewScopeContextValue>(
    () => ({
      scope,
      setScope,
      headerValue: serializeForHeader(scope),
      isOverride: scope.kind !== "self",
    }),
    [scope, setScope]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useViewScope(): ViewScopeContextValue {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error("useViewScope must be used inside ViewScopeProvider");
  }
  return ctx;
}
